import { createRouter, createWebHistory } from 'vue-router'
import { useStore } from 'src/store'
import { ref } from 'vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/realm',
      name: 'realms',
      component: () => import('src/pages/RealmPage.vue')
    },
    {
      path: '/',
      name: 'home',
      component: () => import('src/pages/HomePage.vue')
    },
    {
      path: '/realm-management',
      name: 'realm-management',
      meta: {
        breadcrumb: 'Realm',
        requiresAuth: true
      },
      children: [
        // Realm management
        {
          path: 'realm-settings',
          name: 'realm-settings',
          component: () => import('@/components/realmManagement/RealmEdit.vue'),
          meta: {
            breadcrumb: 'Realm',
            requiresAuth: true
          }
        },
        //  Authority data management
        {
          path: 'authority-data',
          name: 'authority-data',
          component: () => import('@/components/authorityDataManagement/AuthorityDataEdit.vue'),
          meta: {
            breadcrumb: 'Fetch authority data',
            requiresAuth: true
          }
        },
        // Access management
        {
          path: 'access-management',
          name: 'access-management',
          component: () => import('@/components/accessManagement/groupsOverview/groupsOverviewView.vue'),
          meta: {
            breadcrumb: 'Access management',
            noLink: true,
            requiresAuth: true
          },
          children: [
            // groups
            {
              path: 'groups',
              name: 'groups',
              component: () => import('@/components/accessManagement/groupsOverview/groupsOverviewView.vue'),
              meta: {
                breadcrumb: 'Groups',
                requiresAuth: true
              },
              children: [
                {
                  path: '',
                  component: () => import('src/components/accessManagement/groupsOverview/groupsOverviewAll.vue'),
                  meta: {
                    requiresAuth: true
                  }
                },
                {
                  path: 'new',
                  name: 'newGroup', // Name is necessary for the breadcrumb to be included
                  component: () => import('src/components/accessManagement/groupsOverview/GroupCreate.vue'),
                  meta: {
                    breadcrumb: 'New group',
                    requiresAuth: true
                  }
                },
                {
                  path: ':id',
                  name: 'viewGroup', // Name is necessary for the breadcrumb to be included
                  component: () => import('src/components/accessManagement/groupsOverview/GroupView.vue'),
                  meta: {
                    isDynamic: true,
                    breadcrumb: '',
                    requiresAuth: true
                  },
                  children: [
                    {
                      path: '',
                      components: {
                        default: () =>
                          import(
                            'src/components/accessManagement/groupsOverview/sectionComponents/NameSectionComponent.vue'
                          ),
                        firstTab: () =>
                          import(
                            'src/components/accessManagement/groupsOverview/sectionComponents/NameSectionComponent.vue'
                          ),
                        secondTab: () =>
                          import('src/components/accessManagement/groupsOverview/sectionComponents/GeoListSection.vue'),
                        thirdTab: () =>
                          import('src/components/accessManagement/groupsOverview/sectionComponents/FeatureSection.vue'),
                        fourthTab: () =>
                          import('src/components/accessManagement/groupsOverview/sectionComponents/UserListSection.vue')
                      }
                    }
                  ]
                }
              ]
            },
            // users
            {
              path: 'users',
              name: 'users',
              component: () => import('src/components/accessManagement/usersOverview/usersOverviewView.vue'),
              meta: {
                breadcrumb: 'Users',
                requiresAuth: true
              },
              children: [
                {
                  path: '',
                  component: () => import('src/components/accessManagement/usersOverview/userOverviewAll.vue'),
                  meta: {
                    requiresAuth: true
                  }
                }
              ]
            },
            // default
            {
              path: 'default-access',
              name: 'default-access',
              component: () => import('src/components/accessManagement/defaultAccess/DefaultAccessView.vue'),
              meta: {
                breadcrumb: 'Default access',
                requiresAuth: true
              },
              children: [
                {
                  path: '',
                  components: {
                    default: () =>
                      import('src/components/accessManagement/groupsOverview/sectionComponents/FeatureSection.vue'),
                    firstTab: () =>
                      import('src/components/accessManagement/groupsOverview/sectionComponents/FeatureSection.vue'),
                    secondTab: () =>
                      import('src/components/accessManagement/groupsOverview/sectionComponents/GeoListSection.vue')
                  }
                }
              ]
            },
            {
              path: 'navvis-credentials',
              name: 'navvis-credentials',
              component: () =>
                import('src/components/accessManagement/navvisCredentials/navvisCredentialsOverviewView.vue'),
              meta: {
                breadcrumb: 'Navvis Credentials',
                requiresAuth: true
              },
              children: [
                {
                  path: '',
                  component: () => import('src/components/accessManagement/navvisCredentials/navvisCredentialsAll.vue'),
                  meta: {
                    requiresAuth: true
                  }
                },
                {
                  path: 'new',
                  name: 'newNavvisCredentials',
                  component: () =>
                    import('src/components/accessManagement/navvisCredentials/navvisCredentialsCreate.vue'),
                  meta: {
                    breadcrumb: 'Create new',
                    requiresAuth: true
                  }
                }
              ]
            }
          ]
        }
      ]
    },
    {
      path: '/my-portfolio',
      name: 'my-portfolio',
      meta: {
        breadcrumb: 'My portfolio',
        requiresAuth: true
      },
      children: [
        // Complexes:
        {
          path: 'complexes',
          name: 'complexes',
          component: () => import('src/components/complexes/ComplexesView.vue'),
          meta: {
            breadcrumb: 'Complexes',
            requiresAuth: true
          },
          children: [
            {
              path: '',
              component: () => import('src/components/complexes/ComplexesAll.vue'),
              meta: {
                requiresAuth: true
              }
            },
            {
              path: ':complexId',
              name: 'complexDetails',
              component: () => import('src/components/complexes/ComplexEdit.vue'),
              meta: {
                isDynamic: true,
                requiresAuth: true
              },
              children: [
                {
                  path: '',
                  components: {
                    default: () => import('src/components/complexes/sections/ComplexGeneral.vue'),
                    firstTab: () => import('src/components/complexes/sections/ComplexGeneral.vue'),
                    secondTab: () => import('src/components/complexes/sections/ComplexLocation.vue'),
                    thirdTab: () => import('src/components/complexes/sections/ComplexRelatedRealEstates.vue'),
                    fourthTab: () => import('src/components/complexes/sections/ComplexExternalSources.vue')
                  }
                }
              ]
            },
            {
              path: 'new',
              name: 'newComplex',
              component: () => import('src/components/complexes/ComplexCreate.vue'),
              meta: {
                breadcrumb: 'Create new',
                requiresAuth: true
              }
            }
          ]
        },

        // Real Estates:
        {
          path: 'real-estates',
          name: 'realestates',
          component: () => import('src/components/real-estate/RealEstatesView.vue'),
          meta: {
            breadcrumb: 'Real estates',
            requiresAuth: true
          },
          children: [
            {
              path: '',
              component: () => import('src/components/real-estate/RealEstatesAll.vue'),
              meta: {
                requiresAuth: true
              }
            },
            {
              path: ':realEstateId',
              name: 'realEstateDetails',
              component: () => import('src/components/real-estate/RealEstateEditView.vue'),
              meta: {
                isDynamic: true,
                requiresAuth: true
              },
              children: [
                {
                  path: '',
                  components: {
                    default: () => import('src/components/real-estate/sections/RealEstateGeneral.vue'),
                    firstTab: () => import('src/components/real-estate/sections/RealEstateGeneral.vue'),
                    secondTab: () => import('src/components/real-estate/sections/RealEstateLocation.vue'),
                    thirdTab: () => import('src/components/real-estate/sections/RealEstate2DVisuals.vue'),
                    fourthTab: () => import('src/components/real-estate/sections/RealEstateRealityCapture.vue'),
                    fifthTab: () => import('src/components/real-estate/sections/RealEstateAreaMeasurements.vue'),
                    sixthTab: () => import('src/components/real-estate/sections/RealEstateRelatedConstructions.vue')
                  }
                }
              ]
            },
            {
              path: 'new',
              name: 'newRealEstate',
              component: () => import('src/components/real-estate/RealEstateCreate.vue'),
              meta: {
                breadcrumb: 'Create new',
                requiresAuth: true
              }
            }
          ]
        },

        // Constructions:
        {
          path: 'constructions',
          name: 'constructions',
          component: () => import('src/components/constructions/ConstructionsView.vue'),
          meta: {
            breadcrumb: 'Constructions',
            requiresAuth: true
          },
          children: [
            {
              path: '',
              component: () => import('src/components/constructions/ConstructionsAll.vue'),
              meta: {
                requiresAuth: true
              }
            },
            {
              path: 'new',
              name: 'newConstruction',
              component: () => import('src/components/constructions/ConstructionCreate.vue'),
              meta: {
                breadcrumb: 'Create new',
                requiresAuth: true
              }
            },
            {
              path: ':constructionId',
              name: 'constructionsDetails',
              component: () => import('src/components/constructions/ConstructionEditView.vue'),
              meta: {
                isDynamic: true,
                breadcrumb: 'Edit',
                requiresAuth: true
              }
            },
            {
              path: ':constructionId/levels/:floorId',
              name: 'levelDetails',
              component: () => import('src/components/floors/FloorEditView.vue')
            },
            {
              path: ':constructionId/levels/new-level',
              name: 'newLevel',
              component: () => import('src/components/floors/FloorCreate.vue')
            },
            {
              path: ':constructionId/levels/:floorId/rooms/:roomId',
              name: 'roomDetails',
              component: () => import('src/components/rooms/RoomEditView.vue')
            },
            {
              path: ':constructionId/levels/:floorId/rooms/new-room',
              name: 'newRoom',
              component: () => import('src/components/rooms/RoomCreate.vue')
            }
          ]
        }
        // {
        //   path: '/zones',
        //   name: 'zones',
        //   component: () => import('src/components/zones/ZonesView.vue'),
        //   meta: {
        //     breadcrumb: 'Zones',
        //     requiresAuth: true
        //   },
        //   children: [
        //     {
        //       path: '',
        //       component: () => import('src/components/zones/ZonesAll.vue'),
        //       meta: {
        //         requiresAuth: true
        //       }
        //     }
        //   ]
        // }
      ]
    },
    {
      path: '/elements',
      name: 'elements',
      meta: {
        breadcrumb: 'elements',
        requiresAuth: true
      },
      children: [
        // pois
        {
          path: 'POIs',
          name: 'POIs',
          component: () => import('src/components/pois/PoisView.vue'),
          meta: {
            breadcrumb: 'POIs',
            requiresAuth: true
          },
          children: [
            {
              path: '',
              component: () => import('src/components/pois/PoisAll.vue'),
              meta: {
                requiresAuth: true
              }
            },
            {
              path: 'import',
              name: 'import',
              component: () => import('src/components/pois/PoiImport.vue'),
              meta: {
                breadcrumb: 'Import',
                requiresAuth: true
              }
            },
            {
              path: 'categories',
              name: 'poiCategories',
              component: () => import('src/components/pois/PoiSettings.vue'),
              meta: {
                breadcrumb: 'categories',
                requiresAuth: true
              }
            },
            {
              path: 'categories/new',
              name: 'newPoiCategories',
              component: () => import('src/components/pois/PoiSettingsCreate.vue'),
              meta: {
                breadcrumb: 'Create new',
                requiresAuth: true
              }
            }
          ]
        },
        // zones:

        {
          path: 'zones',
          name: 'zones',
          component: () => import('src/components/zones/ZonesView.vue'),
          meta: {
            breadcrumb: 'Zones',
            requiresAuth: true
          },
          children: [
            {
              path: '',
              component: () => import('src/components/zones/ZonesAll.vue'),
              meta: {
                requiresAuth: true
              }
            },
            {
              path: 'categories',
              name: 'zoneCategories',
              component: () => import('src/components/zones/ZoneSettings.vue'),
              meta: {
                breadcrumb: 'categories',
                requiresAuth: true
              }
            },
            {
              path: ':zoneId',
              name: 'zoneDetails',
              component: () => import('src/components/zones/ZoneEditView.vue'),
              meta: {
                isDynamic: true,
                breadcrumb: 'ZoneEdit',
                requiresAuth: true
              }
            },
            {
              path: 'categories/new',
              name: 'newZoneCategories',
              component: () => import('src/components/zones/ZoneSettingsCreate.vue'),
              meta: {
                breadcrumb: 'Create new',
                requiresAuth: true
              }
            }
          ]
        }
      ]
    }
  ]
})

router.beforeEach(async (to, from, next) => {
  const store = useStore()
  if (to.meta.requiresAuth && !store.getLoginStatus()) {
    next({ name: 'realms' })
  } else {
    next()
  }
})

router.afterEach((to) => {
  const store = useStore()
  const activePage = ref<string[]>([])
  activePage.value = to.path.split('/', 4)
  if (activePage.value[1].length === 0) {
    activePage.value[1] = 'home'
  }
  const oldActivePage = store.getActiveMenuItem()
  if (oldActivePage != activePage.value) {
    store.updateActiveMenuItem(activePage.value)
  }
})

export default router
